import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import React, { Suspense, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { wentWrong } from "../../shared/assets/images/images";
import { Image } from "../../../../components/Image/Image";

// Types
type Maybe<T> = T | null | undefined;

type QueryBoundariesProps = {
  children: React.ReactNode;
  loaderClassName?: Maybe<string>;
  errorClassName?: Maybe<string>;
  isPageError?: boolean;
};

// Spinner
const LoadingView = ({ className }: { className?: Maybe<string> }) => {
  return (
    <div className="flex flex-col items-center justify-center gap-3 h-screen relative">
      <div className="w-20 h-20 m-0.5 bg-gray-200 rounded-full animate-pulse"></div>
      <div className="text-gray-600 text-sm">Загрузка...</div>
    </div>
  );
};

// Error + retry with preserved design
const ErrorView = ({
  error,
  resetErrorBoundary,
  className,
  isPageError,
}: FallbackProps & {
  className?: Maybe<string>;
  isPageError?: boolean;
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  // Close ErrorView if either back/forward browser's buttons clicked
  const originalPathname = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== originalPathname.current) {
      resetErrorBoundary();
    }
  }, [location.pathname, resetErrorBoundary]);

  const safeGoBack = () => {
    if (history?.length > 1) {
      history.goBack();
    } else {
      history.replace("/home3");
    }
    resetErrorBoundary();
  };

  const handleReload = () => {
    resetErrorBoundary();
  };

  return (
    <div className="flex flex-col items-center justify-center gap-3 h-screen relative">
      <Image src={wentWrong} className="w-20 h-20 m-0.5" />
      <div className="flex flex-col items-center justify-center  ">
        <div className="text-2xl font-bold text-gray-400">{t("Sorry, something went wrong!")}</div>
        <div className="text-gray-900">{t("Please try again")}</div>
        <div className="text-gray-900">{t("or go back")}</div>
      </div>
      <div className="flex items-center justify-center gap-2">
        <button className="px-4 py-2 rounded-2xl bg-primary text-center w-[8.945rem]" onClick={safeGoBack}>
          {t("Back")}
        </button>
        <button onClick={handleReload} className="px-4 py-2 rounded-2xl bg-primary ">
          {t("Reload")}
        </button>
      </div>
    </div>
  );
};

export const QueryBoundaries = ({ children, loaderClassName, errorClassName, isPageError }: QueryBoundariesProps) => {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          FallbackComponent={({ error, resetErrorBoundary }) => (
            <ErrorView
              error={error}
              resetErrorBoundary={resetErrorBoundary}
              className={errorClassName}
              isPageError={isPageError}
            />
          )}
        >
          <Suspense fallback={<LoadingView className={loaderClassName} />}>{children}</Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
};
